// ═══════════════════════════════════════════════════════════════
// Marketplace.jsx — verticalstack XC-MKT Marketplace surface
// Path: src/components/Marketplace.jsx
//
// S9.A (June 30, 2026) — NEW FILE. Fifth unlocked Cross-cutting
// platform component (after XC-DOC S5, XC-MSG S6, XC-TEAM S7.A,
// XC-NET S8.B). Sibling of Network.jsx — deliberately mirrors its
// idiom (banner / note / KPI grid / XcFilterBar / search card /
// candidate cards / VendorProfilePanel inline detail).
//
// S9.B (June 30, 2026) — EDIT. Wire the search to the rewritten
// matchSubcontractors CF (AI-06, §6 schema, redeployed S9.B). The
// httpsCallable result is the PRIMARY engine; the client-side
// matchVendors matcher (S8.F) is now the OFFLINE fallback — used only
// when the CF throws (unreachable / auth / error). A CF success that
// returns [] is a legitimate "no match" → T3 fallback band, not a
// fallback to the local matcher. CF returns a lean payload
// ({vendorId,name,tier,matchScore,reasoning}); we re-hydrate vendorId
// against the roster already in state so both engines render the same
// card. An engine badge shows which path produced the results.
//
// S9.D (June 30, 2026) — EDIT. Wire the Hire CTA (first vendor WRITE path)
//   to the new inviteVendor CF (v2). Hire flips a result vendor's /vendors
//   doc onboardingState: prospect -> invited, job-attached to this workspace.
//   RE-SCOPE NOTE: S9.A/B comments anticipated S9.D = the T3 OPEN-POOL path
//   (Google Places lookup + cold-onboard a provider NOT yet in the roster).
//   The S9 Close Handoff re-scoped S9.D to inviteVendor only, which acts on
//   vendors ALREADY in /vendors. Every ranked result carries a real roster
//   id, so Hire wires there; the open-pool/no-roster-doc case stays a later
//   XC-NET pipeline step (bands relabeled below). Stripe Connect is stubbed
//   (pending activation, T4) — the invite is recorded now.
//
// WHAT IT IS
//   The TRANSACTIONAL surface — "Who/what do I need RIGHT NOW for THIS
//   job?" Per Strategy §3f LOCKED boundary: Network = WHO EXISTS ·
//   Marketplace = HIRE NOW · Team = WHO'S ASSIGNED. Marketplace searches
//   the SAME platform-wide /vendors roster Network surfaces, but answers
//   a different question: not "who is in my ecosystem" but "rank the
//   ecosystem for THIS job, right now." Every search is a demand-capture
//   event; the T3 cold-invite path is how the network grows itself.
//
// TIER FALLBACK (design intent, sandbox v7.2 xcMarketplace)
//   T1 (Network-verified) → T2 (community-adjacent) → T3 (Google Places +
//   cold invite). The AI dispatch model (matchSubcontractors) ranks T1/T2
//   from the live roster; the T3 open-pool path (Google Places lookup +
//   cold invite) is NOT built here — it lands in S9.D (job-attached
//   invite). Where no verified match exists, the surface shows the T3
//   fallback affordance honestly, muted, labeled with its session.
//
// S9.B SCOPE
//   • Search calls matchSubcontractors (AI-06) via httpsCallable; the
//     matchVendors client matcher is the offline fallback on CF error.
//   • KPIs render DORMANT ("—") except the live searchable-pool count —
//     no fires/transaction collection exists yet (AS-S9-MKT-KPI-DORMANT).
//   • Stage-agnostic — Cross-cutting nav has no stage context, same as
//     Network shipped (AS-S9-MKT-STAGE-AGNOSTIC).
//   • Materials category → deferred empty state (workOrders dep, S13).
//   • "Hire / invite" CTA → WIRED in S9.D to inviteVendor (see S9.D note
//     above). "View" reuses VendorProfilePanel (S8.C) inline.
//
// DATA READ
//   getDocs(collection(db,'vendors')) — platform-wide, rules v3.4
//   (read = any signed-in member). Canonical §6 vendor shape (S8.A).
//   matchSubcontractors reads the same /vendors server-side.
//
// FILTER
//   <XcFilterBar viewId="xc-marketplace"> — already registered in
//   xcFilterBar.js (Category: All/Vendors/Materials/Services/Inspections ·
//   Tier: All/T1/T2/T3). Tier post-filters the candidate results;
//   Category=Materials routes to the deferred state.
//
// PROPS
//   workspace / roles / roleLabel — workspace.workspaceId is passed to the
//     CF as optional diagnostic context (which workspace fired the demand).
//   mockMode — when true, renders a hardcoded multi-tier vendor array for
//     the ?xcmkt-smoke=1 harness. In mockMode the CF is NOT called — the
//     search uses the local matchVendors matcher directly so the harness
//     stays offline (no auth / no network).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import XcFilterBar from './xcFilterBar';
import VendorProfilePanel from './VendorProfilePanel';
import { matchVendors } from './matchVendors';

// ── Tier presentation (shared idiom with Network.jsx) ────────────────────
const TIER_META = {
  T1: { label: 'T1', descriptor: 'verified',  color: '#0A5C42', bg: '#E8F7F2', sub: 'prior relationship + transactions' },
  T2: { label: 'T2', descriptor: 'adjacent',  color: '#7A4A08', bg: '#FDF3E3', sub: 'community-vouched' },
  T3: { label: 'T3', descriptor: 'open pool', color: '#5A6478', bg: '#F0F2F5', sub: 'Google Places + cold invites' },
};

// onboardingState values that already mean invited/onboarded — mirrors the
// inviteVendor CF guard. Hire on any of these shows a muted 'In network'
// state (the invite primitive is a no-op; job assignment lands with S13).
const ALREADY_IN_PIPELINE = new Set(['invited', 'onboarding', 'active', 'onboarded', 'verified']);

function prettyTrade(t) {
  return String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Mock roster for ?xcmkt-smoke=1 harness (shape parity with Network) ────
const MOCK_VENDORS = [
  { id: 'vendor-aps-mgmt', name: 'APS Management', tier: 'T1', trades: ['property_management', 'general_maintenance', 'turnover'], area: 'Canton, OH', insurance: true, w9OnFile: false, onboardingState: 'active', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: 'Larry Brown', phone: '330-327-7742' } },
  { id: 'vendor-everguard', name: 'EverGuard LLC', tier: 'T1', trades: ['hvac', 'plumbing', 'general_repair'], area: 'Marietta, OH', insurance: true, w9OnFile: true, onboardingState: 'active', rating: 4.8, jobs: 23, response: 2, firstFix: 88, stripe: 'acct_mock_eg', paymentTerms: 'Net 15', contact: { name: 'Seth', phone: '—' }, signals: { external: { bbb: 4.5, google: 4.9, yelp: 4.7 }, responsiveness: { avgResponseHours: 2 }, completion: { completionRate: 95, firstFixRate: 88 }, recency: { lastActivityAt: Date.now() - 10 * 86400000 } } },
  { id: 'vendor-sikora', name: 'Sikora Law LLC', tier: 'T1', trades: ['legal', '1031_qi', 'closing_attorney'], area: 'Columbus, OH', insurance: true, w9OnFile: true, onboardingState: 'active', rating: 5.0, jobs: 4, response: 6, firstFix: 100, stripe: null, paymentTerms: 'Net 30', contact: { name: 'Mike Sikora', phone: '—' }, signals: { external: { google: 5.0, yelp: 5.0 }, responsiveness: { avgResponseHours: 6 }, completion: { completionRate: 100, firstFixRate: 100 }, recency: { lastActivityAt: Date.now() - 30 * 86400000 } } },
  { id: 'vendor-stark', name: 'Stark Electric', tier: 'T2', trades: ['electrical'], area: 'Canton, OH', insurance: true, w9OnFile: false, onboardingState: 'onboarding', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: '(local)', phone: '—' } },
  { id: 'vendor-tristate', name: 'TriState Mechanical', tier: 'T2', trades: ['hvac', 'mep'], area: 'Canton, OH', insurance: false, w9OnFile: false, onboardingState: 'invited', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: '(local)', phone: '—' } },
  { id: 'vendor-va-forester', name: 'TBD-VA Forester', tier: 'T3', trades: ['forester_timber'], area: 'Montgomery Co, VA', insurance: false, w9OnFile: false, onboardingState: 'prospect', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: '—', phone: '—' } },
];

// ── Component ────────────────────────────────────────────────────────────
export default function Marketplace({ workspace, roles, roleLabel, mockMode = false }) {
  const [vendors, setVendors] = useState(mockMode ? MOCK_VENDORS : []);
  const [loading, setLoading] = useState(!mockMode);
  const [error, setError] = useState(null);
  const [filterValues, setFilterValues] = useState({ category: 'All', tier: 'All' });
  const [selectedVendor, setSelectedVendor] = useState(null);

  // ── Search state ───────────────────────────────────────────────────────
  const [jobCategory, setJobCategory] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [results, setResults] = useState(null); // null = no search run yet
  const [searching, setSearching] = useState(false);
  const [engine, setEngine] = useState(null); // 'ai' (CF) | 'offline' (matchVendors)
  const [inviteState, setInviteState] = useState({}); // { [vendorId]: {status,alreadyInvited?,msg?,mock?} } — S9.D

  useEffect(() => {
    if (mockMode) return;
    let cancelled = false;

    async function loadVendors() {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDocs(collection(db, 'vendors'));
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setVendors(list);
      } catch (err) {
        if (cancelled) return;
        console.error('[Marketplace] vendors load error:', err);
        setError(err.message || 'Failed to load vendors');
        setVendors([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadVendors();
    return () => { cancelled = true; };
  }, [mockMode]);

  // ── Vendor profile detail (early return) — reuses VendorProfilePanel ────
  if (selectedVendor) {
    return (
      <VendorProfilePanel
        vendor={selectedVendor}
        onBack={() => setSelectedVendor(null)}
      />
    );
  }

  const handleView = (v) => setSelectedVendor(v);

  // ── Hire → inviteVendor CF (S9.D · first vendor WRITE path) ─────────────
  // Flips a ranked result's /vendors doc prospect → invited, job-attached to
  // this workspace. matchSubcontractors returns roster vendorIds, so every
  // result row carries a valid /vendors id even when the full doc isn't in
  // local state. Idempotent server-side (alreadyInvited for in-pipeline docs).
  const handleHire = async (vendor) => {
    const vid = vendor.id;
    setInviteState((s) => ({ ...s, [vid]: { status: 'inviting' } }));

    // Harness stays offline — simulate the write, never call the CF.
    if (mockMode) {
      setInviteState((s) => ({ ...s, [vid]: { status: 'invited', mock: true } }));
      return;
    }

    const workspaceId = workspace?.workspaceId || null;
    if (!workspaceId) {
      setInviteState((s) => ({ ...s, [vid]: { status: 'error', msg: 'No workspace context — open Marketplace from a workspace to invite.' } }));
      return;
    }

    try {
      const call = httpsCallable(functions, 'inviteVendor');
      const res = await call({ vendorId: vid, workspaceId });
      const d = res?.data || {};
      setInviteState((s) => ({ ...s, [vid]: { status: 'invited', alreadyInvited: !!d.alreadyInvited } }));
    } catch (err) {
      console.error('[Marketplace] inviteVendor failed:', err);
      setInviteState((s) => ({ ...s, [vid]: { status: 'error', msg: err?.message || 'Invite failed' } }));
    }
  };

  // ── Derive job-category options from the live roster's trades ───────────
  const categoryOptions = Array.from(
    new Set(vendors.flatMap((v) => v.trades || []))
  )
    .map((t) => ({ value: t, label: prettyTrade(t) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // ── Offline matcher → normalized result rows (shared shape) ────────────
  const offlineRank = () =>
    matchVendors({ jobCategory, serviceArea }, vendors, { topN: 8 })
      .map((s) => ({ vendor: s.vendor, score: s.score, reasons: s.reasons }));

  // ── Run search: CF (AI-06) primary, matchVendors offline fallback ──────
  const runSearch = async () => {
    if (!jobCategory) { setResults([]); setEngine(null); return; }
    setSearching(true);

    let rows;
    let usedEngine;

    if (mockMode) {
      // Harness stays offline — no CF call (no auth / no network).
      rows = offlineRank();
      usedEngine = 'offline';
    } else {
      try {
        const call = httpsCallable(functions, 'matchSubcontractors');
        const res = await call({
          jobCategory,
          serviceArea: serviceArea || null,
          workspaceId: workspace?.workspaceId || null,
        });
        const matches = res?.data && Array.isArray(res.data.matches) ? res.data.matches : [];
        rows = matches.map((m) => {
          const full = vendors.find((v) => v.id === m.vendorId);
          return {
            vendor: full || { id: m.vendorId, name: m.name, tier: m.tier, area: '' },
            score: m.matchScore,
            reasons: m.reasoning ? [m.reasoning] : [],
          };
        });
        usedEngine = 'ai';
      } catch (err) {
        // CF unreachable / auth / error → offline matcher.
        console.warn('[Marketplace] matchSubcontractors CF failed, using offline matcher:', err?.message);
        rows = offlineRank();
        usedEngine = 'offline';
      }
    }

    // Tier post-filter (from the XcFilterBar) applies to either engine.
    if (filterValues.tier !== 'All') {
      rows = rows.filter((r) => (r.vendor.tier || 'T3') === filterValues.tier);
    }

    setEngine(usedEngine);
    setResults(rows);
    setSearching(false);
  };

  // ── KPIs (dormant except live searchable-pool count) ───────────────────
  const kpis = [
    { label: 'Fires MTD',      value: '—',             sub: 'no transaction data yet' },
    { label: 'T1 conversion',  value: '—',             sub: 'verified vendor reached', accent: true },
    { label: 'T3 onboarded',   value: '—',             sub: 'cold → community' },
    { label: 'Network pool',   value: vendors.length,  sub: 'searchable candidates' },
  ];

  const materialsSelected = filterValues.category === 'Materials';
  const anyInvited = Object.values(inviteState).some((x) => x && x.status === 'invited'); // S9.D

  return (
    <div style={styles.container}>
      {/* ── Banner ────────────────────────────────────────────────── */}
      <div style={styles.banner}>
        <div>
          <span style={styles.bannerTitle}>Marketplace</span>
          <span style={styles.bannerTag}>XC-MKT</span>
        </div>
        <div style={styles.bannerMuted}>
          Transactional surface · "Who/what do I need RIGHT NOW for THIS job?" · T1 → T2 → T3 search over the Network roster
        </div>
      </div>

      {/* ── Page head ─────────────────────────────────────────────── */}
      <div style={styles.pageHead}>
        <div>
          <h1 style={styles.pageTitle}>Marketplace</h1>
          <div style={styles.pageSub}>
            Search the network for this job · ranked T1 → T2 → T3
          </div>
        </div>
        <div style={styles.pageActions}>
          <button type="button" style={styles.ghostBtn} disabled title="Fire log — transactional history, Tier 3">
            ⎕ Fire log
          </button>
          <button
            type="button"
            style={styles.primaryBtn}
            onClick={() => {
              const el = document.getElementById('mkt-job-category');
              if (el) el.focus();
            }}
            title="Start a new search"
          >
            + New search
          </button>
        </div>
      </div>

      {/* ── XC-MKT vs XC-NET distinction note ─────────────────────── */}
      <div style={styles.noteCard}>
        <b>XC-MKT vs XC-NET:</b> Marketplace is the transactional layer over the same roster Network surfaces.
        Network answers "who is in my ecosystem"; Marketplace answers "rank the ecosystem for THIS job, right now."
        Every search is a demand-capture event — and the T3 path is how the network grows itself.
      </div>

      {/* ── T1 → T2 → T3 fallback explainer (sandbox xcMarketplace) ─── */}
      <div style={styles.explainerBand}>
        <b>Marketplace is the transactional layer.</b> A job search ranks candidates&nbsp;
        <span style={styles.t1Chip}>T1 · Network-verified</span> →&nbsp;
        <span style={styles.t2Chip}>T2 · community-adjacent</span> →&nbsp;
        <span style={styles.t3Chip}>T3 · Google Places + cold invite</span>.&nbsp;
        Ranking runs on the AI dispatch model (matchSubcontractors · AI-06) with the client matcher as offline
        fallback. <b>Hire</b> invites a ranked roster vendor (prospect → invited) via inviteVendor (S9.D);
        onboarding a provider not yet in the roster (open-pool T3) is a later XC-NET step.
      </div>

      {/* ── KPI grid (dormant per AS-S9-MKT-KPI-DORMANT) ──────────── */}
      <div style={styles.kpiGrid}>
        {kpis.map((k) => (
          <div key={k.label} style={{ ...styles.kpiCard, ...(k.accent ? styles.kpiCardAccent : {}) }}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
            <div style={styles.kpiSub}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Filter bar (xc-marketplace viewId, registered) ────────── */}
      <XcFilterBar viewId="xc-marketplace" onChange={setFilterValues} />

      {/* ── Materials deferred state OR the search surface ────────── */}
      {materialsSelected ? (
        <div style={styles.deferredCard}>
          <div style={styles.cardHeader}>Materials marketplace</div>
          <div style={styles.deferredBody}>
            The materials catalog (Home Depot · Lowe's · Amazon, affiliate-tracked) attaches to work orders and
            lands at <b>S13</b> alongside the workOrders collection it depends on. Switch the Category filter back
            to <b>Vendors</b>, <b>Services</b>, or <b>Inspections</b> to search the provider network.
          </div>
        </div>
      ) : (
        <div style={styles.searchCard}>
          <div style={styles.cardHeader}>Find a provider for this job</div>
          <div style={styles.searchIntro}>
            Pick a job category and (optionally) an area — the AI dispatch model (AI-06) ranks the roster by
            trade match, tier, rating, and area. The client matcher (S8.F) is the offline fallback if the
            dispatch model is unreachable.
          </div>

          <div style={styles.searchForm}>
            <select
              id="mkt-job-category"
              value={jobCategory}
              onChange={(e) => setJobCategory(e.target.value)}
              style={styles.searchSelect}
            >
              <option value="">Job category…</option>
              {categoryOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={serviceArea}
              onChange={(e) => setServiceArea(e.target.value)}
              placeholder="Service area (e.g. Canton, OH) — optional"
              style={styles.searchInput}
            />
            <button
              type="button"
              style={(jobCategory && !searching) ? styles.searchBtn : styles.searchBtnDisabled}
              onClick={runSearch}
              disabled={!jobCategory || searching}
            >
              {searching ? 'Ranking…' : 'Search'}
            </button>
          </div>

          {/* ── Results ─────────────────────────────────────────── */}
          {error ? (
            <div style={styles.errorState}>Could not load the network: {error}</div>
          ) : loading ? (
            <div style={styles.loadingState}>Loading network…</div>
          ) : searching ? (
            <div style={styles.loadingState}>Ranking candidates…</div>
          ) : results === null ? (
            <div style={styles.promptState}>
              Run a search to rank candidates from the {vendors.length}-vendor network.
            </div>
          ) : results.length === 0 ? (
            <div style={styles.t3FallbackBand}>
              <div style={styles.t3FallbackTitle}>No verified or community match for that job{filterValues.tier !== 'All' ? ` at ${filterValues.tier}` : ''}.</div>
              <div style={styles.t3FallbackBody}>
                This is where the <b>T3 open pool</b> fires — a Google Places lookup plus a cold-onboard for a
                provider not yet in your roster. That open-pool path is a later XC-NET pipeline step; <b>S9.D</b>
                delivered the invite primitive (Hire) for vendors already in the roster. For now, broaden the
                job category or clear the tier filter.
              </div>
            </div>
          ) : (
            <>
              <div style={styles.engineRow}>
                {engine === 'ai' ? (
                  <span style={styles.engineBadgeAi}>AI dispatch · AI-06</span>
                ) : (
                  <span style={styles.engineBadgeOffline}>Offline matcher · S8.F</span>
                )}
                <span style={styles.engineNote}>
                  {engine === 'ai'
                    ? 'ranked server-side by matchSubcontractors'
                    : 'dispatch model unreachable — local fallback'}
                </span>
              </div>
              <div style={styles.results}>
                {results.map((s, i) => {
                  const tm = TIER_META[s.vendor.tier] || TIER_META.T3;
                  return (
                    <div key={s.vendor.id} style={styles.resultRow}>
                      <span style={styles.rank}>#{i + 1}</span>
                      <span style={{ ...styles.tierBadge, color: tm.color, background: tm.bg }}>
                        {tm.label}
                      </span>
                      <div style={styles.resultMain}>
                        <div style={styles.resultName}>{s.vendor.name}</div>
                        <div style={styles.resultReasons}>
                          {s.vendor.area || '—'} · {s.reasons.join(' · ')}
                        </div>
                        {inviteState[s.vendor.id]?.status === 'error' && (
                          <div style={styles.inviteError}>⚠ {inviteState[s.vendor.id].msg}</div>
                        )}
                      </div>
                      <span style={styles.resultScore}>{s.score}</span>
                      <button
                        type="button"
                        style={styles.viewBtn}
                        onClick={() => handleView(s.vendor)}
                        title="View vendor profile"
                      >
                        View
                      </button>
                      {(() => {
                        const st = inviteState[s.vendor.id];
                        const pipeline = ALREADY_IN_PIPELINE.has(String(s.vendor.onboardingState || '').trim());
                        if (st?.status === 'inviting') {
                          return <button type="button" style={styles.hireBtnPending} disabled>Inviting…</button>;
                        }
                        if (st?.status === 'invited') {
                          return (
                            <button type="button" style={styles.hireBtnInvited} disabled
                              title={st.alreadyInvited ? 'Already in your network' : 'Invited — prospect → invited (Stripe pending, T4)'}>
                              {st.alreadyInvited ? 'In network ✓' : 'Invited ✓'}
                            </button>
                          );
                        }
                        if (st?.status === 'error') {
                          return (
                            <button type="button" style={styles.hireBtn} onClick={() => handleHire(s.vendor)}
                              title={st.msg || 'Retry invite'}>Retry</button>
                          );
                        }
                        if (pipeline) {
                          return (
                            <button type="button" style={styles.hireBtnInvited} disabled
                              title="Already in your network — job assignment lands with work orders (S13)">
                              In network ✓
                            </button>
                          );
                        }
                        return (
                          <button type="button" style={styles.hireBtn} onClick={() => handleHire(s.vendor)}
                            title="Invite this vendor to your network (prospect → invited) for this workspace">
                            Hire
                          </button>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
              {anyInvited && (
                <div style={styles.stripeAffordance}>
                  Payments — Stripe Connect <b>pending activation</b> (T4). Invited vendors begin payment
                  onboarding when Connect goes live; the invite itself (prospect → invited) is recorded now.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Inline styles (migration-epoch idiom: Arial, navy/cyan, inline) ──────
const NAVY = '#003060';
const CYAN = '#00B8D0';
const styles = {
  container: { maxWidth: 1100, margin: '0 auto', fontFamily: 'Arial, sans-serif', color: '#1F2937' },
  banner: {
    backgroundColor: '#F0F7FA', border: `1px solid ${CYAN}33`, borderRadius: 8,
    padding: '12px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: 8,
  },
  bannerTitle: { fontSize: 14, fontWeight: 700, color: NAVY },
  bannerTag: {
    display: 'inline-block', marginLeft: 8, padding: '2px 8px', fontSize: 10, fontWeight: 700,
    color: CYAN, background: `${CYAN}1F`, borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4,
  },
  bannerMuted: { fontSize: 12, color: '#6B7280' },
  pageHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 16, flexWrap: 'wrap', gap: 12,
  },
  pageTitle: { fontSize: 24, fontWeight: 700, color: NAVY, margin: 0 },
  pageSub: { fontSize: 13, color: '#6B7280', marginTop: 4 },
  pageActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  ghostBtn: {
    background: '#FFFFFF', border: '1px solid #D0D5DD', color: '#9CA3AF',
    padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    cursor: 'not-allowed', fontFamily: 'inherit',
  },
  primaryBtn: {
    background: NAVY, border: 'none', color: '#FFFFFF', padding: '8px 16px', borderRadius: 6,
    fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  noteCard: {
    background: '#F8FAFC', border: '1px solid #E5E8EE', borderRadius: 8,
    padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#374151', lineHeight: 1.55,
  },
  explainerBand: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderLeft: `3px solid ${CYAN}`,
    borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#374151', lineHeight: 1.7,
  },
  t1Chip: { fontSize: 11, fontWeight: 700, color: '#0A5C42', background: '#E8F7F2', padding: '2px 8px', borderRadius: 10, fontFamily: 'Consolas, monospace' },
  t2Chip: { fontSize: 11, fontWeight: 700, color: '#7A4A08', background: '#FDF3E3', padding: '2px 8px', borderRadius: 10, fontFamily: 'Consolas, monospace' },
  t3Chip: { fontSize: 11, fontWeight: 700, color: '#5A6478', background: '#F0F2F5', padding: '2px 8px', borderRadius: 10, fontFamily: 'Consolas, monospace' },
  kpiGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14, marginBottom: 16,
  },
  kpiCard: { background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16 },
  kpiCardAccent: { border: `1px solid ${CYAN}`, boxShadow: '0 2px 8px rgba(0,184,208,0.10)' },
  kpiLabel: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  kpiValue: { fontSize: 28, fontWeight: 700, color: NAVY, margin: '6px 0 2px' },
  kpiSub: { fontSize: 11, color: '#9CA3AF' },
  cardHeader: {
    fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#9CA3AF',
    textTransform: 'uppercase', marginBottom: 12,
  },
  searchCard: {
    background: '#FFFFFF', border: `1px solid ${CYAN}55`, borderRadius: 10, padding: 16, marginBottom: 20,
  },
  searchIntro: { fontSize: 12, color: '#6B7280', lineHeight: 1.55, marginBottom: 14 },
  searchForm: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 },
  searchSelect: {
    padding: '8px 10px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 13,
    fontFamily: 'inherit', color: '#1F2937', background: '#FFFFFF', minWidth: 180,
  },
  searchInput: {
    padding: '8px 10px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 13,
    fontFamily: 'inherit', color: '#1F2937', flex: 1, minWidth: 200,
  },
  searchBtn: {
    background: NAVY, border: 'none', color: '#FFFFFF', padding: '9px 20px', borderRadius: 6,
    fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  searchBtnDisabled: {
    background: '#D0D5DD', border: 'none', color: '#FFFFFF', padding: '9px 20px', borderRadius: 6,
    fontSize: 13, fontWeight: 700, cursor: 'not-allowed', fontFamily: 'inherit',
  },
  promptState: { marginTop: 14, fontSize: 13, color: '#6B7280', fontStyle: 'italic' },
  engineRow: { marginTop: 14, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  engineBadgeAi: {
    fontSize: 10, fontWeight: 700, color: CYAN, background: `${CYAN}1F`,
    padding: '3px 10px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4,
  },
  engineBadgeOffline: {
    fontSize: 10, fontWeight: 700, color: '#7A4A08', background: '#FDF3E3',
    padding: '3px 10px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4,
  },
  engineNote: { fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' },
  results: { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 },
  resultRow: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
    background: '#F8FAFC', borderRadius: 8, border: '1px solid #EEF1F5',
  },
  rank: { fontSize: 13, fontWeight: 700, color: CYAN, minWidth: 22 },
  resultMain: { flex: 1, minWidth: 0 },
  resultName: { fontSize: 13, fontWeight: 700, color: NAVY },
  resultReasons: { fontSize: 11, color: '#6B7280', marginTop: 2 },
  resultScore: { fontSize: 18, fontWeight: 700, color: NAVY, minWidth: 32, textAlign: 'right' },
  tierBadge: {
    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10, fontFamily: 'Consolas, monospace',
  },
  viewBtn: {
    background: '#FFFFFF', border: `1px solid ${CYAN}`, color: CYAN, padding: '5px 12px',
    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  // S9.D Hire states
  hireBtn: {
    background: CYAN, border: 'none', color: '#FFFFFF', padding: '5px 12px',
    borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  hireBtnPending: {
    background: '#CFEFF4', border: 'none', color: '#0A5C42', padding: '5px 12px',
    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'wait', fontFamily: 'inherit',
  },
  hireBtnInvited: {
    background: '#E8F7F2', border: '1px solid rgba(10,92,66,0.20)', color: '#0A5C42', padding: '5px 12px',
    borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'default', fontFamily: 'inherit',
  },
  inviteError: { fontSize: 11, color: '#B91C1C', marginTop: 3 },
  stripeAffordance: {
    marginTop: 12, fontSize: 11, color: '#6B7280', lineHeight: 1.5,
    background: '#FFFDF5', border: '1px solid #F0E6C8', borderRadius: 6, padding: '8px 12px',
  },
  t3FallbackBand: {
    marginTop: 14, background: '#F0F2F5', border: '1px solid #E0E4EA', borderRadius: 8, padding: '14px 16px',
  },
  t3FallbackTitle: { fontSize: 13, fontWeight: 700, color: '#5A6478', marginBottom: 4 },
  t3FallbackBody: { fontSize: 12, color: '#6B7280', lineHeight: 1.55 },
  deferredCard: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderLeft: '3px solid #9CA3AF',
    borderRadius: 10, padding: 16, marginBottom: 20,
  },
  deferredBody: { fontSize: 13, color: '#6B7280', lineHeight: 1.6 },
  loadingState: { color: '#9CA3AF', fontStyle: 'italic', fontSize: 13, padding: '16px 4px' },
  errorState: {
    color: '#B91C1C', fontSize: 13, padding: '12px 16px', backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5', borderRadius: 6,
  },
};
